<template>
  <div class="container-fluid admin-page">
    <div class="title-area">
      <h2>動画タグ管理</h2>
      <div class="create-btn">
        <button class="btn btn-info" @click="openCreateModal">
          <i class="fas fa-plus"></i><span class="ml-1">新規作成</span>
        </button>
      </div>
    </div>
    <TagTable
      :tags="state.tags"
      @edit="openUpdateModal"
      @destroy="onDestroyTag"
      @merge="openMergeModal"
      @search="loadTags"
    />
    <TagEditModal
      v-if="state.showEditModal"
      :is-edit-mode="state.isEditMode"
      :tag="state.editingTag"
      :groups2="state.groups2"
      :groups3="state.groups3"
      :is-requesting="state.isRequesting"
      :http-error-msgs="state.httpErrorMsgs"
      @create="onCreateTag"
      @update="onUpdateTag"
      @close="state.showEditModal = false"
    />
    <TagMergeModal
      v-if="state.mergingTag"
      :source-tag="state.mergingTag"
      :tags="state.tags"
      @merge="onMergeTag"
      @close="state.mergingTag = null"
    />
    <Modal
      v-if="state.showErrorModal"
      :title="errorModalTitle"
      :show-bottom-dismiss-button="false"
      @close="state.showErrorModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p2 font-mtx-desc" v-html="errorModalMsg" />
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import group2Api from 'src/apis/group2';
import group3Api from 'src/apis/group3';
import tagApi from 'src/apis/tag';
import {
  computed,
  onMounted,
  reactive,
} from 'vue';
import { useAuth } from 'src/composables/useAuth';
import TagTable from 'src/components/Admin/Tag/TagTable.vue';
import TagEditModal from 'src/components/Admin/Tag/TagEditModal.vue';
import TagMergeModal from 'src/components/Admin/Tag/TagMergeModal.vue';
import type { AdminTagIndexItem } from 'src/models/tag';
import type { AdminGroup2 } from 'src/models/group2';
import type { AdminGroup3 } from 'src/models/group3';
import type { TagInputModel } from 'src/models/tag';
import { createTagInitInputModel } from 'src/models/tag';
import type { AdminTagCreateParams, AdminTagUpdateParams } from 'src/models/apis/tag/adminTagRequest';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import type { AxiosError } from 'axios';

interface TagsPageState {
  tags: AdminTagIndexItem[];
  groups2: AdminGroup2[];
  groups3: AdminGroup3[];
  editingTag: TagInputModel;
  showEditModal: boolean;
  isEditMode: boolean;
  isRequesting: boolean;
  showErrorModal: boolean;
  httpErrorMsgs: string[];
  mergingTag: AdminTagIndexItem | null;
}

const state = reactive<TagsPageState>({
  tags: [],
  groups2: [],
  groups3: [],
  editingTag: createTagInitInputModel(),
  showEditModal: false,
  isEditMode: false,
  isRequesting: false,
  showErrorModal: false,
  httpErrorMsgs: [],
  mergingTag: null,
});

const { user } = useAuth();
const g2id = computed<number | null>(() => user?.groupInfo?.g2id ?? null);
const g3id = computed<number | null>(() => user?.groupInfo?.g3id ?? null);

const loadTags = async(): Promise<void> => {
  const tags = await tagApi.adminIndex({ tag_name: null });
  if (!tags) return;

  state.tags = tags;
};

const openCreateModal = (): void => {
  state.isEditMode = false;
  state.editingTag = {
    ...createTagInitInputModel(),
    group2Id: g2id.value,
    group3Id: g3id.value,
  };
  state.httpErrorMsgs = [];
  state.showEditModal = true;
};

const openUpdateModal = (tag: AdminTagIndexItem): void => {
  state.isEditMode = true;
  state.editingTag = {
    id: tag.id,
    tagName: tag.tagName,
    group2Id: tag.group2Id,
    group3Id: tag.group3Id,
  };
  state.httpErrorMsgs = [];
  state.showEditModal = true;
};

const onCreateTag = async(payload: AdminTagCreateParams): Promise<void> => {
  state.isRequesting = true;
  try {
    await tagApi.adminCreate(payload);
    state.httpErrorMsgs = [];
    await loadTags();
    state.showEditModal = false;
  } catch (err) {
    state.httpErrorMsgs = getErrorMessages(err as AxiosError);
  } finally {
    state.isRequesting = false;
  }
};

const onUpdateTag = async(id: number, payload: AdminTagUpdateParams): Promise<void> => {
  state.isRequesting = true;
  try {
    await tagApi.adminUpdate(id, payload);
    state.httpErrorMsgs = [];
    await loadTags();
    state.showEditModal = false;
  } catch (err) {
    state.httpErrorMsgs = getErrorMessages(err as AxiosError);
  } finally {
    state.isRequesting = false;
  }
};

const onDestroyTag = async(tag: AdminTagIndexItem): Promise<void> => {
  try {
    await tagApi.adminDestroy(tag.id);
    await loadTags();
  } catch {
    state.showErrorModal = true;
  }
};

const openMergeModal = (tag: AdminTagIndexItem): void => {
  state.mergingTag = tag;
};

const onMergeTag = async(payload: { sourceId: number; targetId: number }): Promise<void> => {
  try {
    await tagApi.adminNameIdentify({
      id: payload.sourceId,
      name_identify_to_tag_id: payload.targetId,
    });
    state.mergingTag = null;
    await loadTags();
  } catch {
    state.mergingTag = null;
    state.showErrorModal = true;
  }
};

onMounted(async() => {
  const [groups2, groups3] = await Promise.all([
    group2Api.adminIndex(),
    group3Api.adminIndex(),
  ]);
  state.groups2 = groups2;
  state.groups3 = groups3;
  await loadTags();
});

const errorModalTitle = 'エラー';
const errorModalMsg = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';
</script>

<style lang="scss"  scoped>
</style>
